import { Link } from "react-router-dom";
import { Heart, Truck, Users, Package, Award, ArrowRight, MessageCircle, Instagram, Facebook, MapPin } from "lucide-react";
import { useEffect } from "react";
import { useSettings } from "@/context/SettingsContext";
import { useSEO } from "@/lib/seo";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/store/Reveal";

const STATS = [
  { icon: Award, value: "7 años", label: "de trayectoria" },
  { icon: Package, value: "+1200", label: "productos en catálogo" },
  { icon: Truck, value: "Todo el país", label: "envíos en Costa Rica" },
  { icon: Heart, value: "10%", label: "de utilidades donadas" },
];

const VALUES = [
  { icon: Users, title: "Empresa familiar", text: "Somos una familia tica apasionada por los animales. Cada cliente es parte de nuestra manada." },
  { icon: Heart, title: "Compromiso social", text: "Donamos el 10% de nuestras utilidades a casas cuna y programas de cuido de animales en Costa Rica." },
  { icon: Award, title: "Calidad garantizada", text: "Seleccionamos cada producto pensando en la salud y felicidad de tu mascota." },
];

export default function QuienesSomos() {
  const { settings } = useSettings();
  useEffect(() => {
    if (window.location.hash === "#contacto") {
      setTimeout(() => document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);
  useSEO({
    title: "Quiénes somos | The Pet Shopping Co Costa Rica",
    description: "The Pet Shopping Co es una empresa familiar tica con 7 años de trayectoria, más de 1200 productos y envíos a todo Costa Rica. Donamos el 10% de utilidades a casas cuna.",
    canonicalPath: "/nosotros",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "Quiénes somos - The Pet Shopping Co",
      description: "Empresa familiar de mascotas en Costa Rica con 7 años de trayectoria.",
    },
  });

  return (
    <div>
      {/* hero */}
      <section className="relative h-[52vh] min-h-[360px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1450778869180-41d0601e046e?crop=entropy&cs=srgb&fm=jpg&auto=format&fit=crop&w=1100&q=68" alt="Perro y gato" fetchpriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 to-charcoal/20" />
        <div className="relative z-10 h-full max-w-5xl mx-auto px-5 flex flex-col justify-end pb-12">
          <Reveal>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-cream/80">Nuestra historia</span>
            <h1 className="font-display text-4xl sm:text-6xl font-bold text-cream tracking-tight mt-3 max-w-2xl leading-[1.02]">Más que una tienda, una familia</h1>
          </Reveal>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 py-16 sm:py-24">
        <Reveal>
          <p className="font-display text-2xl sm:text-3xl text-charcoal leading-relaxed tracking-tight">
            En <strong className="text-coral">{settings.store_name}</strong> llevamos <strong>7 años</strong> acompañando a las familias costarricenses y a sus mascotas con productos de calidad, precios justos y mucho cariño.
          </p>
          <p className="text-charcoal/75 mt-6 text-lg leading-relaxed">
            Empezamos como un pequeño negocio familiar y hoy contamos con un catálogo de más de <strong>1200 productos</strong> y envíos a todo el país. Pero lo que más nos enorgullece es nuestra campaña social: donamos el <strong className="text-coral">10% de nuestras utilidades</strong> a casas cuna y programas de cuido de animales. Cada compra tuya ayuda a un animalito que lo necesita. 🐾
          </p>
        </Reveal>
      </section>

      {/* stats */}
      <section className="bg-charcoal text-cream">
        <StaggerGroup className="max-w-6xl mx-auto px-5 py-16 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <StaggerItem key={s.label} className="text-center">
              <s.icon className="w-8 h-8 mx-auto text-coral mb-3" />
              <div className="font-display text-3xl sm:text-4xl font-bold">{s.value}</div>
              <div className="text-cream/60 text-sm mt-1">{s.label}</div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>

      {/* values */}
      <section className="max-w-6xl mx-auto px-5 py-16 sm:py-24">
        <Reveal><span className="text-xs font-semibold tracking-[0.25em] uppercase text-coral">Lo que nos mueve</span><h2 className="font-display text-3xl sm:text-5xl font-bold text-charcoal tracking-tight mt-3">Nuestros valores</h2></Reveal>
        <StaggerGroup className="grid sm:grid-cols-3 gap-5 mt-10">
          {VALUES.map((v) => (
            <StaggerItem key={v.title}>
              <div className="h-full bg-white rounded-3xl border border-[#E3E7E2] p-7 card-shadow">
                <span className="grid place-items-center w-12 h-12 rounded-2xl bg-peach text-coral mb-4"><v.icon className="w-6 h-6" /></span>
                <h3 className="font-display text-xl font-bold text-charcoal">{v.title}</h3>
                <p className="text-charcoal/75 mt-2 leading-relaxed">{v.text}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>

      <section className="max-w-6xl mx-auto px-5 pb-24">
        <Reveal>
          <div className="rounded-[2rem] bg-peach/60 border border-[#E3E7E2] p-10 sm:p-16 text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-charcoal tracking-tight">Compra con propósito</h2>
            <p className="text-charcoal/75 mt-3 max-w-lg mx-auto">Cada producto que llevas a casa ayuda a los animalitos sin hogar de Costa Rica.</p>
            <Link to="/tienda" className="mt-6 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-charcoal text-cream font-semibold hover:bg-coral transition-colors">Explorar la tienda <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </Reveal>
      </section>

      {/* ============ CONTACTO (fusionado) ============ */}
      <section id="contacto" className="max-w-6xl mx-auto px-5 pb-24 scroll-mt-24">
        <Reveal className="text-center max-w-2xl mx-auto mb-8">
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-coral">Estamos para ayudarte</span>
          <h2 className="font-display text-3xl sm:text-5xl font-bold text-charcoal tracking-tight mt-3">Contáctanos</h2>
          <p className="text-muted-moss mt-3">¿Dudas sobre un producto o tu pedido? Escríbenos, te respondemos rapidito.</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-4">
          <Reveal>
            <a href={`https://wa.me/${settings.whatsapp_number}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="group block h-full rounded-3xl bg-coral text-white p-8 card-shadow hover:bg-coral-dark transition-colors">
              <MessageCircle className="w-9 h-9 mb-4" />
              <h3 className="font-display text-2xl font-bold">WhatsApp</h3>
              <p className="text-white/85 mt-1">La forma más rápida de pedir y coordinar tu entrega.</p>
              <p className="mt-4 font-semibold">+{settings.whatsapp_number}</p>
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl bg-white border border-[#E3E7E2] p-8 card-shadow flex flex-col gap-4">
              <div className="flex items-center gap-3"><MapPin className="w-6 h-6 text-teal" /><span className="text-charcoal font-semibold">{settings.country}</span></div>
              <div className="border-t border-[#E3E7E2] pt-4">
                <p className="text-sm text-muted-moss mb-3">Síguenos como <strong>@petshoppingcompany</strong></p>
                <div className="flex gap-2">
                  <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="grid place-items-center w-11 h-11 rounded-xl bg-peach text-coral hover:bg-coral hover:text-white transition-colors"><Instagram className="w-5 h-5" /></a>
                  <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="grid place-items-center w-11 h-11 rounded-xl bg-peach text-coral hover:bg-coral hover:text-white transition-colors"><Facebook className="w-5 h-5" /></a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
